import type {
  CarrierCommentDocument,
  CommentBullet,
} from "./buildCommentDocument";

export interface ClipboardContent {
  html: string;
  text: string;
}

interface ClipboardSection {
  heading: string;
  bullets: readonly CommentBullet[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Splits a plan group's bullets into one clipboard section per plan option
 * when the group is dual/tri-option (per the AC: "Plan option name is
 * appended when the plan group is dual-option or tri-option"). Sections
 * appear in first-bullet order — the group's benefit-table row order already
 * orders each option's own bullets, so this only decides which option block
 * comes first when both appear.
 */
const splitByPlanOption = (
  label: string,
  bullets: readonly CommentBullet[],
  optionLabelByProposalPlanId: ReadonlyMap<string, string>,
): ClipboardSection[] => {
  const optionLabelOf = (bullet: CommentBullet): string =>
    (bullet.proposalPlanId &&
      optionLabelByProposalPlanId.get(bullet.proposalPlanId)) ||
    "";

  const isMultiOption = bullets.some((bullet) => optionLabelOf(bullet));
  if (!isMultiOption) {
    return [{ heading: label, bullets }];
  }

  const bulletsByOption = new Map<string, CommentBullet[]>();
  for (const bullet of bullets) {
    const optionLabel = optionLabelOf(bullet);
    const group = bulletsByOption.get(optionLabel) ?? [];
    group.push(bullet);
    bulletsByOption.set(optionLabel, group);
  }

  return [...bulletsByOption.entries()].map(([optionLabel, optionBullets]) => ({
    heading: optionLabel ? `${label} (${optionLabel} Plan)` : label,
    bullets: optionBullets,
  }));
};

const sectionsFor = (
  carrier: CarrierCommentDocument,
  optionLabelByProposalPlanId: ReadonlyMap<string, string>,
): ClipboardSection[] => [
  ...carrier.coverages.flatMap((coverage) =>
    coverage.planGroups.flatMap((planGroup) =>
      splitByPlanOption(
        planGroup.label,
        planGroup.bullets,
        optionLabelByProposalPlanId,
      ),
    ),
  ),
  ...carrier.comps.map((section) => ({
    heading: section.label,
    bullets: section.bullets,
  })),
  ...carrier.responses.map((section) => ({
    heading: section.label,
    bullets: section.bullets,
  })),
];

const sectionToHtml = (section: ClipboardSection): string => {
  const items = section.bullets
    .map(
      (bullet) =>
        `<li>${escapeHtml(bullet.benefitLabel)}<ul><li>${escapeHtml(bullet.comment)}</li></ul></li>`,
    )
    .join("");
  return `<p><strong>${escapeHtml(section.heading)}</strong></p><ul>${items}</ul>`;
};

const sectionToText = (section: ClipboardSection): string => {
  const items = section.bullets
    .map((bullet) => `- ${bullet.benefitLabel}\n  - ${bullet.comment}`)
    .join("\n");
  return `${section.heading}\n${items}`;
};

/**
 * Formats one carrier's Needs Review comments for the copy-paste clipboard.
 * HTML carries the bullet nesting Outlook needs to preserve formatting on
 * paste; plain text is the `text/plain` fallback for targets that don't
 * accept HTML.
 */
export const formatForClipboard = (
  carrier: CarrierCommentDocument,
  optionLabelByProposalPlanId: ReadonlyMap<string, string>,
): ClipboardContent => {
  const sections = sectionsFor(carrier, optionLabelByProposalPlanId);

  return {
    html: sections.map(sectionToHtml).join(""),
    text: sections.map(sectionToText).join("\n\n"),
  };
};
